import React, { useEffect, useState, useRef, useCallback } from 'react';
import { apiTokensApi } from '../../services/api';
import { formatDateTime } from './formatDate';
import { AlertCircle, Check, Copy, KeyRound, Loader2, Plus, ShieldOff, Trash2, X } from 'lucide-react';

function formatDate(value) {
  return formatDateTime(value, 'Never');
}

export default function ApiTokenManagement() {
  const [tokens, setTokens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [name, setName] = useState('');
  const [creating, setCreating] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newToken, setNewToken] = useState(null);
  const [copied, setCopied] = useState('');
  const fetchId = useRef(0);

  const fetchTokens = useCallback(async () => {
    const id = ++fetchId.current;
    try {
      setError('');
      const data = await apiTokensApi.getAll();
      if (id !== fetchId.current) return;
      if (!Array.isArray(data) || data.some(token => !token.id || typeof token.name !== 'string')) {
        throw new Error('Invalid API token data from server');
      }
      setTokens(data);
    } catch (err) {
      if (id === fetchId.current) setError(err.message);
    } finally {
      if (id === fetchId.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTokens();
    return () => { fetchId.current += 1; };
  }, [fetchTokens]);

  async function copyText(value, key) {
    await navigator.clipboard.writeText(value);
    setCopied(key);
    setTimeout(() => setCopied(''), 1800);
  }

  async function createToken(e) {
    e.preventDefault();
    if (!name.trim() || creating) return;

    try {
      setCreating(true);
      const token = await apiTokensApi.create(name.trim());
      setNewToken(token);
      setName('');
      setShowCreateModal(false);
      await fetchTokens();
    } catch (err) {
      alert(err.message);
    } finally {
      setCreating(false);
    }
  }

  async function deleteToken(id) {
    if (!window.confirm('Delete this revoked API token from the list?')) return;
    try {
      await apiTokensApi.delete(id);
      await fetchTokens();
    } catch (err) {
      alert(err.message);
    }
  }

  async function revokeToken(id) {
    if (!window.confirm('Revoke this API token? Existing chatbot jobs using it will stop working.')) return;
    try {
      await apiTokensApi.revoke(id);
      await fetchTokens();
    } catch (err) {
      alert(err.message);
    }
  }

  const externalUrl = apiTokensApi.externalPoliciesUrl();

  if (loading) {
    return (
      <div className="animate-pulse space-y-3">
        <div className="h-8 bg-gray-200 rounded w-48" />
        <div className="h-24 bg-gray-100 rounded-xl" />
        {[1, 2, 3].map(i => <div key={i} className="h-12 bg-gray-100 rounded-xl" />)}
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="w-16 h-16 rounded-2xl bg-red-100 flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-red-500" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Failed to load API tokens</h2>
          <p className="text-gray-500 mb-6">{error}</p>
          {newToken?.token && (
            <div className="mb-6 max-w-md rounded-lg border border-green-200 bg-green-50 p-3 text-left">
              <p className="text-sm font-medium text-green-900">Copy your newly created token now</p>
              <code className="mt-2 block break-all text-xs text-gray-700">{newToken.token}</code>
              <button onClick={() => copyText(newToken.token, 'new-token')} className="mt-2 text-sm text-green-700 underline">Copy token</button>
            </div>
          )}
          <button onClick={fetchTokens} className="px-6 py-3 bg-[#00AAFF] text-white font-medium rounded-xl hover:bg-[#0088cc] transition-colors">Try Again</button>
        </div>
      </div>
    );
  }

  return (
    <div className="animate-fade-in space-y-4">
      <div className="flex items-center justify-between gap-4">
        <div>
          <span className="text-xs font-medium text-gray-500 uppercase tracking-widest">API Tokens</span>
          <h1 className="mt-1 text-xl font-bold text-gray-900">Chatbot Policy Access</h1>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="inline-flex items-center justify-center gap-1.5 h-8 px-3 bg-[#00AAFF] hover:bg-[#0088cc] text-white text-xs font-medium rounded-lg transition-colors"
        >
          <Plus className="w-3.5 h-3.5" />
          Create Token
        </button>
      </div>

      <div className="bg-white rounded-2xl border border-gray-200 p-4 shadow-sm">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div className="min-w-0">
            <p className="text-xs font-medium text-gray-400 uppercase tracking-wider">External policies endpoint</p>
            <p className="mt-1 text-sm text-gray-700 break-all">{externalUrl}</p>
          </div>
          <button
            onClick={() => copyText(externalUrl, 'endpoint')}
            className="inline-flex items-center justify-center gap-2 px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium rounded-lg transition-colors"
          >
            {copied === 'endpoint' ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
            Copy
          </button>
        </div>
      </div>

      {newToken && (
        <div className="bg-green-50 border border-green-200 rounded-2xl p-4">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="font-semibold text-green-900">Copy this token now</p>
              <p className="mt-1 text-sm text-green-700">For security, the full token is shown only once.</p>
              <div className="mt-3 flex items-center gap-2 rounded-xl bg-white border border-green-200 p-2">
                <code className="min-w-0 flex-1 text-xs text-gray-700 break-all">{newToken.token}</code>
                <button
                  onClick={() => copyText(newToken.token, 'new-token')}
                  className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-green-100 hover:bg-green-200 text-green-700 transition-colors"
                >
                  {copied === 'new-token' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <button onClick={() => setNewToken(null)} className="p-2 rounded-lg hover:bg-green-100 text-green-700">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-sm">
        {tokens.length === 0 ? (
          <div className="py-12 text-center text-sm text-gray-400">
            <KeyRound className="w-10 h-10 mx-auto mb-3 text-gray-300" />
            No API tokens yet
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50">
                <th className="text-left text-xs font-medium text-gray-400 uppercase tracking-wider px-4 py-3">Name</th>
                <th className="text-left text-xs font-medium text-gray-400 uppercase tracking-wider px-4 py-3">Token</th>
                <th className="text-left text-xs font-medium text-gray-400 uppercase tracking-wider px-4 py-3">Last Used</th>
                <th className="text-left text-xs font-medium text-gray-400 uppercase tracking-wider px-4 py-3">Status</th>
                <th className="text-right text-xs font-medium text-gray-400 uppercase tracking-wider px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {tokens.map(token => {
                const revoked = !!token.revoked_at;
                return (
                  <tr key={token.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">{token.name}</div>
                      <div className="text-xs text-gray-400">Created {formatDate(token.created_at)}</div>
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-gray-500">{token.token_preview}</td>
                    <td className="px-4 py-3 text-xs text-gray-500">{formatDate(token.last_used_at)}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${revoked ? 'bg-gray-100 text-gray-500' : 'bg-green-100 text-green-700'}`}>
                        {revoked ? 'Revoked' : 'Active'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      {!revoked && (
                        <button
                          onClick={() => revokeToken(token.id)}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-red-600 hover:bg-red-50 transition-colors"
                        >
                          <ShieldOff className="w-3.5 h-3.5" />
                          Revoke
                        </button>
                      )}
                      {revoked && (
                        <button
                          onClick={() => deleteToken(token.id)}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-red-600 hover:bg-red-50 transition-colors"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                          Delete
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 animate-fade-in" onClick={() => !creating && setShowCreateModal(false)}>
          <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl animate-slide-up" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-5 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-[#00AAFF]/10 flex items-center justify-center">
                  <KeyRound className="w-5 h-5 text-[#00AAFF]" />
                </div>
                <h2 className="text-lg font-bold text-gray-900">Create API Token</h2>
              </div>
              {!creating && (
                <button onClick={() => setShowCreateModal(false)} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              )}
            </div>

            <form onSubmit={createToken} className="p-5 space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Token Name</label>
                <input
                  autoFocus
                  type="text"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  placeholder="Chatbot Indexer"
                  className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:border-[#00AAFF] focus:ring-1 focus:ring-[#00AAFF]"
                />
              </div>

              <div className="flex items-center justify-end gap-3">
                <button type="button" onClick={() => setShowCreateModal(false)} disabled={creating} className="px-5 py-2.5 text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-xl transition-all disabled:opacity-40">
                  Cancel
                </button>
                <button type="submit" disabled={!name.trim() || creating} className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#00AAFF] hover:bg-[#0088cc] text-white font-medium rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                  {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                  Create
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
